import { trackSaveProperty } from '../../../../../analytics/event-tracking/trackSaveProperty';
import {
  mapPropertyRelationshipToEnum,
  PropertyRelationship,
} from '../../../../../components/PropertyRelationship/PropertyRelationship';
import { useFollowedProperties } from '../../../../hooks/useFollowedProperties';
import { usePropertyId } from '../../../../../contexts/PropertyIdContext/PropertyIdContext';
import { usePropertyPageQuery } from '../../../../graphql/usePropertyPageQuery';
import { UpdatePropertyRelationshipModal } from '../../../../../components/PropertyRelationship/UpdatePropertyRelationshipModal/UpdatePropertyRelationshipModal';
import { updatePropertyRelationshipModalPageSource } from '../../../../../components/PropertyRelationship/UpdatePropertyRelationshipModal/UpdatePropertyRelationshipModalPageSource';
import type { FunctionComponent } from 'react';

interface UpdatePropertyRelationshipModalWithDataProps {
  opened: boolean;
  onClose: () => void;
  defaultRelationship?: boolean;
}

export const UpdatePropertyRelationshipModalWithData: FunctionComponent<
  UpdatePropertyRelationshipModalWithDataProps
> = ({ opened, onClose, defaultRelationship }) => {
  const cachedQuery = usePropertyPageQuery();
  const followPropertyModal = cachedQuery?.data.propertyPageBySlugV3.followPropertyModalV2;
  const { followedProperties, updatePropertyRelationship, unfollowProperty, loading } =
    useFollowedProperties();
  const propertyId = usePropertyId();

  const propertyRelationship = followedProperties?.find(
    ({ id }) => id === propertyId,
  )?.relationship;

  const propertyRelationshipEnum = mapPropertyRelationshipToEnum(propertyRelationship);

  if (!followPropertyModal || !propertyRelationshipEnum) return null;

  const title = 'You’re following this property';
  const { subtitle, image } = followPropertyModal;

  const onUpdatePropertyRelationship = (newPropertyRelationship: PropertyRelationship) => {
    void updatePropertyRelationship(propertyId, newPropertyRelationship);
    trackSaveProperty({
      user_action: 'update',
      in_page_source: [
        'property_page',
        updatePropertyRelationshipModalPageSource,
        'update_property_button',
      ].join('__'),
      property_relationship: newPropertyRelationship,
    });
    onClose();
  };

  const onUnfollowProperty = () => {
    void unfollowProperty(propertyId);
    trackSaveProperty({
      user_action: 'unsave',
      in_page_source: [
        'property_page',
        updatePropertyRelationshipModalPageSource,
        'unfollow_property_button',
      ].join('__'),
      property_relationship: null,
    });
    onClose();
  };

  return (
    <UpdatePropertyRelationshipModal
      title={title}
      subtitle={subtitle}
      image={image}
      propertyRelationship={propertyRelationshipEnum}
      opened={opened}
      onClose={onClose}
      onSubmitUpdate={onUpdatePropertyRelationship}
      onSubmitUnfollow={onUnfollowProperty}
      loading={loading}
      defaultSelection={defaultRelationship ? PropertyRelationship.myHome : undefined}
    />
  );
};
